/* =========================================================================
   Telemetry Helper & Transformer Utilities
   ========================================================================= */

import {
  TelemetryPanelConfig,
  TelemetryCatChannelType,
  TelemetryUnitSystem,
  TelemetryViewportState,
  TelemetrySeries,
  TelemetryDataPoint,
} from '../types/telemetry-dashboard.types';
import { CAT, getSubParams } from './telemetry-catalog.utils';
import { conv, unitOf, fmt } from './telemetry-unit.utils';
import {
  getTelemetryHashCode,
  getTelemetrySubColor,
  getTelemetryVizLabel,
  isTelemetryChartViz,
  getTelemetrySeriesColor,
  TELEMETRY_SERIES_PALETTE,
} from './telemetry-color.utils';

// Color Palettes (Aliased to canonical 65-color dark-mode palette for full backward compatibility)
export const PAL = [...TELEMETRY_SERIES_PALETTE];
export const CPAL = [...TELEMETRY_SERIES_PALETTE];
export { getTelemetrySeriesColor, TELEMETRY_SERIES_PALETTE };

export const CHART_MAX_UNITS = 2;

// Grid layout constants
export const COLS = 12;
export const ROW = 74;
export const GAP = 4;
export const PAD = 16;

// Viewport boundary constants
export const MIN_SPAN_MS = 2 * 60000;
export const MAX_SPAN_MS = 3 * 3600000;
export const MAX_HISTORY_MS = 24 * 3600000;

/**
 * Resolves local storage key prioritizing uniqueId over truetwinId.
 */
export function getTelemetryStorageKey(uniqueId?: string, truetwinId?: string): string {
  const primary = uniqueId && uniqueId !== 'config' && uniqueId !== 'telemetry-config' ? uniqueId : null;
  const keyIdentifier = primary || truetwinId || 'default';
  return `telemetry_workspaces_config_${keyIdentifier}`;
}

/** Aliases delegating to canonical telemetry-color.utils */
export const hashCode = getTelemetryHashCode;
export const subColor = getTelemetrySubColor;
export const vizLabel = getTelemetryVizLabel;
export const isChartViz = isTelemetryChartViz;

/** Formats window timestamp depending on timeframe window size */
export function fmtWinTime(ms: number, spanMs: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  if (spanMs <= 10 * 60000) {
    return p(d.getUTCHours()) + ':' + p(d.getUTCMinutes()) + ':' + p(d.getUTCSeconds());
  }
  if (spanMs <= 6 * 3600000) {
    return p(d.getUTCHours()) + ':' + p(d.getUTCMinutes());
  }
  return (d.getUTCMonth() + 1) + '/' + d.getUTCDate() + ' ' + p(d.getUTCHours()) + ':' + p(d.getUTCMinutes());
}

/** Formats tooltip timestamp ensuring seconds (HH:mm:ss) are always present across all timeframes */
export function fmtTooltipTimestamp(ms: number, spanMs?: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  const timeStr = p(d.getUTCHours()) + ':' + p(d.getUTCMinutes()) + ':' + p(d.getUTCSeconds());
  if (spanMs && spanMs > 24 * 3600000) {
    return (d.getUTCMonth() + 1) + '/' + d.getUTCDate() + ' ' + timeStr;
  }
  return timeStr;
}

/** Formats timestamp as HH:mm:ss clock string */
export function fmtClock(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return p(d.getUTCHours()) + ':' + p(d.getUTCMinutes()) + ':' + p(d.getUTCSeconds());
}

/** Human-readable timeframe label */
export function spanLabel(ms: number): string {
  const m = ms / 60000;
  if (m < 1) return Math.round(ms / 1000) + 's window';
  if (m < 60) return (Math.round(m * 10) / 10) + ' min window';
  return (Math.round(m / 6) / 10) + ' hr window';
}

/** Human-readable offset ago label (seconds, minutes, hours) */
export function agoLabel(ms: number): string {
  const sec = Math.round(ms / 1000);
  if (sec < 60) return (sec <= 0 ? 1 : sec) + 's';
  const m = ms / 60000;
  if (m < 60) return (Math.round(m * 10) / 10) + 'm';
  const h = m / 60;
  return (Math.round(h * 10) / 10) + 'h';
}

export interface WindowSeries {
  name: string;
  color: string;
  data: number[];
  _def?: TelemetryCatChannelType;
}

export interface WindowBundle {
  def: TelemetryCatChannelType;
  series: WindowSeries[];
  times: string[];
  spanMs: number;
  endMs: number;
  startMs?: number;
}

let activeTelemetrySeriesStore: TelemetrySeries[] = [];
let globalSimulationStartTimeMs: number | null = null;

export function setGlobalSimulationStartTimeMs(ms: number | null) {
  globalSimulationStartTimeMs = ms;
}

export function getGlobalSimulationStartTimeMs(): number | null {
  return globalSimulationStartTimeMs;
}

let activeTelemetrySeriesMap = new Map<string, TelemetrySeries>();

export function updateGlobalTelemetrySeriesStore(series: TelemetrySeries[]) {
  activeTelemetrySeriesStore = series || [];
  activeTelemetrySeriesMap.clear();

  if (Array.isArray(series)) {
    for (let i = 0; i < series.length; i++) {
      const s = series[i];
      if (!s || !s.id) continue;
      const sId = s.id;
      const sLower = sId.toLowerCase();
      const sDotLower = sId.replace(':', '.').toLowerCase();
      const sColonLower = sId.replace('.', ':').toLowerCase();

      activeTelemetrySeriesMap.set(sId, s);
      activeTelemetrySeriesMap.set(sLower, s);
      activeTelemetrySeriesMap.set(sDotLower, s);
      activeTelemetrySeriesMap.set(sColonLower, s);

      if (sId.includes(':')) {
        const clean = sId.split(':').pop()!;
        activeTelemetrySeriesMap.set(clean, s);
        activeTelemetrySeriesMap.set(clean.toLowerCase(), s);
      }
      if (sId.includes('.')) {
        const clean = sId.split('.').pop()!;
        activeTelemetrySeriesMap.set(clean, s);
        activeTelemetrySeriesMap.set(clean.toLowerCase(), s);
      }
      const sName = (s as any)?.name;
      if (sName && typeof sName === 'string') {
        activeTelemetrySeriesMap.set(sName, s);
        activeTelemetrySeriesMap.set(sName.toLowerCase(), s);
      }
    }
  }
}

export function getActiveTelemetrySeriesStore(): TelemetrySeries[] {
  return activeTelemetrySeriesStore;
}

/** Formats raw parameter keys with underscores into uppercase labels with spaces */
export function formatParameterLabel(rawLabel: string): string {
  if (!rawLabel) return 'Parameter';
  return rawLabel.replace(/_/g, ' ');
}

export function getSubsystemLabel(sub: string): string {
  return CAT[sub]?.label || sub || 'Subsystem';
}

export function getChannelWarn(def?: TelemetryCatChannelType | null): number | null {
  if (!def) return null;
  const th = def.threshold;
  if (th?.warn && th.warn.length > 0) {
    const w = th.warn[0];
    return w.min !== undefined && w.min !== null ? w.min : (w.max !== undefined ? w.max : null);
  }
  return (def as any).warn ?? null;
}

export function getChannelCrit(def?: TelemetryCatChannelType | null): number | null {
  if (!def) return null;
  const th = def.threshold;
  if (th?.crit && th.crit.length > 0) {
    const c = th.crit[0];
    return c.min !== undefined && c.min !== null ? c.min : (c.max !== undefined ? c.max : null);
  }
  return (def as any).crit ?? null;
}

export function getChannelDir(def?: TelemetryCatChannelType | null): string {
  return def?.threshold?.dir || (def as any)?.dir || 'high';
}

const DEFAULT_CHANNEL_DEF: TelemetryCatChannelType = {
  paramId: '',
  uniqueId: '',
  subsystem: '',
  paramName: '',
  dataSource: null,
  label: 'Parameter',
  unit: '',
  unitType: '',
  kind: 'cont',
  min: 0,
  max: 100,
  threshold: { on: false, warn: [], crit: [], dir: 'high' },
  allowed: ['line', 'area', 'kpi', 'gauge', 'table', 'ctable', 'cchart'],
  enumValues: {},
  unified: false,
};

/**
 * Searches the master catalog (CAT) for a channel definition by subsystem and paramId/paramName/uniqueId.
 */
export function findCatalogChannel(sub: string, paramId: string): TelemetryCatChannelType | null {
  if (!sub && !paramId) return null;

  const subParams = getSubParams(sub);
  const cleanParam = paramId.includes(':') ? paramId.split(':').pop()! : paramId;
  const lower = cleanParam.toLowerCase();

  // 1. Search primary subsystem map
  if (subParams) {
    if (subParams[paramId]) return subParams[paramId];
    if (subParams[cleanParam]) return subParams[cleanParam];

    const matchParam = Object.values(subParams).find(
      (ch: any) => ch && (ch.paramId === cleanParam || ch.paramId === paramId || ch.paramName === cleanParam || ch.paramName === paramId || ch.uniqueId === cleanParam || ch.uniqueId === paramId)
    );
    if (matchParam) return matchParam;

    const matchKey = Object.keys(subParams).find((k) => k.toLowerCase() === lower);
    if (matchKey && subParams[matchKey]) return subParams[matchKey];

    const matchLabel = Object.values(subParams).find(
      (ch: any) => ch && (ch.label?.toLowerCase() === lower || ch.name?.toLowerCase() === lower)
    );
    if (matchLabel) return matchLabel;
  }

  // 2. Global search across all subsystems in CAT
  for (const catKey of Object.keys(CAT)) {
    const catParams = getSubParams(catKey);
    if (catParams[paramId]) return catParams[paramId];
    if (catParams[cleanParam]) return catParams[cleanParam];

    const matchParam = Object.values(catParams).find(
      (ch: any) => ch && (ch.paramId === cleanParam || ch.paramId === paramId || ch.paramName === cleanParam || ch.paramName === paramId || ch.uniqueId === cleanParam || ch.uniqueId === paramId)
    );
    if (matchParam) return matchParam;

    const matchKey = Object.keys(catParams).find((k) => k.toLowerCase() === lower);
    if (matchKey && catParams[matchKey]) return catParams[matchKey];
  }

  return null;
}

/**
 * Normalizes any raw/legacy parameter object or (subsystem, paramId) strings into a clean, strongly-typed TelemetryCatChannelType.
 */
export function normalizePanelParameter(input?: any, paramArg?: string): TelemetryCatChannelType {
  if (!input) return DEFAULT_CHANNEL_DEF;

  let raw: any = {};
  if (typeof input === 'object' && input !== null) {
    raw = input;
  } else {
    raw = { subsystem: input, paramId: paramArg };
  }

  const subStr = raw.subsystem || '';
  const paramIdStr = raw.paramId || raw.paramName || paramArg || '';

  const catDef = findCatalogChannel(subStr, paramIdStr);

  const subsystem = subStr || catDef?.subsystem || '';
  const paramId = raw.paramId || catDef?.paramId || paramIdStr;
  const paramName = raw.paramName || catDef?.paramName || paramId;
  const label = raw.label || catDef?.label || formatParameterLabel(paramName || paramId || 'Parameter');
  const uniqueId = raw.uniqueId || raw.uniqueid || catDef?.uniqueId || (subsystem && paramId ? `${subsystem}:${paramId}` : (subsystem && label ? `${subsystem}:${label}` : paramIdStr));

  return {
    paramId,
    uniqueId,
    subsystem,
    paramName,
    dataSource: raw.dataSource ?? catDef?.dataSource ?? null,
    label,
    unit: raw.unit ?? catDef?.unit ?? '',
    unitType: raw.unitType ?? catDef?.unitType ?? '',
    kind: raw.kind ?? catDef?.kind ?? 'cont',
    min: raw.min ?? catDef?.min ?? 0,
    max: raw.max ?? catDef?.max ?? 100,
    threshold: raw.threshold || catDef?.threshold || DEFAULT_CHANNEL_DEF.threshold,
    allowed: (Array.isArray(raw.allowed) && raw.allowed.length > 0)
      ? raw.allowed
      : (catDef?.allowed || DEFAULT_CHANNEL_DEF.allowed),
    enumValues: raw.enumValues || catDef?.enumValues || {},
    unified: raw.unified ?? catDef?.unified ?? false,
    series: raw.series ?? catDef?.series,
    ...(raw.si != null ? { si: raw.si } : {}),
  } as any;
}

/**
 * Legacy lookup alias pointing directly to the unified normalizePanelParameter pipeline.
 */
export const getChannelDef = normalizePanelParameter;

/**
 * Returns the normalized primary TelemetryCatChannelType parameter for a panel.
 */
export function getPanelPrimaryParam(panel?: { parameters?: any[] } | null): TelemetryCatChannelType {
  return normalizePanelParameter(panel?.parameters?.[0]);
}

/**
 * Returns the number of categories in a bar/horizontal bar panel.
 */
export function getBarCategoryCount(panel?: any): number {
  if (!panel) return 0;
  const params = panel.parameters || [];
  if (params.length > 1) return params.length;
  const first = params[0] || {};
  const def = normalizePanelParameter(first);
  if (def?.series && Array.isArray(def.series) && def.series.length > 0) {
    return def.series.length;
  }
  return 1;
}

export function findTelemetrySeriesStore(
  subsystem: string,
  paramId: string,
  uniqueId?: string,
  panelId?: string
): TelemetrySeries | undefined {
  if (!subsystem && !paramId && !uniqueId && !panelId) return undefined;
  if (!activeTelemetrySeriesStore || activeTelemetrySeriesStore.length === 0) return undefined;

  const colonId = uniqueId || (subsystem && paramId ? `${subsystem}:${paramId}` : '');
  const dotId = subsystem && paramId ? `${subsystem}.${paramId}` : '';
  const cleanParam = paramId ? (paramId.includes(':') ? paramId.split(':').pop()! : paramId) : '';
  const cleanUniqueId = uniqueId ? (uniqueId.includes(':') ? uniqueId.split(':').pop()! : uniqueId) : '';

  // 1. Direct O(1) Map Lookups
  const hit =
    (uniqueId && activeTelemetrySeriesMap.get(uniqueId)) ||
    (colonId && activeTelemetrySeriesMap.get(colonId)) ||
    (dotId && activeTelemetrySeriesMap.get(dotId)) ||
    (paramId && activeTelemetrySeriesMap.get(paramId)) ||
    (cleanParam && activeTelemetrySeriesMap.get(cleanParam)) ||
    (cleanUniqueId && activeTelemetrySeriesMap.get(cleanUniqueId)) ||
    (colonId && activeTelemetrySeriesMap.get(colonId.toLowerCase())) ||
    (dotId && activeTelemetrySeriesMap.get(dotId.toLowerCase())) ||
    (cleanParam && activeTelemetrySeriesMap.get(cleanParam.toLowerCase())) ||
    (cleanUniqueId && activeTelemetrySeriesMap.get(cleanUniqueId.toLowerCase())) ||
    (panelId && activeTelemetrySeriesMap.get(panelId));

  if (hit) return hit;

  // 2. Exact fallback scan
  const matched = activeTelemetrySeriesStore.find((s) => {
    if (!s || !s.id) return false;
    const sId = s.id;
    const sLower = sId.toLowerCase();
    return (
      sId === uniqueId ||
      sId === colonId ||
      sId === dotId ||
      sId === paramId ||
      sId === cleanParam ||
      (cleanParam && (sLower.endsWith(`:${cleanParam.toLowerCase()}`) || sLower.endsWith(`.${cleanParam.toLowerCase()}`)))
    );
  });

  if (matched) {
    if (colonId) activeTelemetrySeriesMap.set(colonId, matched);
    if (paramId) activeTelemetrySeriesMap.set(paramId, matched);
    if (uniqueId) activeTelemetrySeriesMap.set(uniqueId, matched);
    return matched;
  }

  return undefined;
}

/** Checks whether a widget panel has any valid telemetry data points in memory */
export function hasPanelTelemetryData(w?: TelemetryPanelConfig | any): boolean {
  if (!w) return false;
  const first = w.parameters?.[0] || { sub: '', tel: '' };
  const params = Array.isArray(w.parameters) && w.parameters.length > 0 ? w.parameters : [first];

  return params.some((p: any) => {
    if (!p) return false;
    const sub = p.subsystem || p.sub || '';
    const paramId = p.paramId || p.paramName || p.tel || '';
    const colonId = p.uniqueId || (sub && paramId ? `${sub}:${paramId}` : '');
    const found = findTelemetrySeriesStore(sub, paramId, colonId, w.id);
    return Boolean(found && found.data && found.data.length > 0);
  });
}

/** Resolves the latest telemetry timestamp for a widget across its parameters or global store */
export function getWidgetLatestTimestamp(w: TelemetryPanelConfig): number {
  let maxTs = 0;
  const params = w?.parameters || [];
  for (const p of params) {
    const sub = (p as any).subsystem || '';
    const paramId = (p as any).paramId || (p as any).paramName || '';
    const colonId = p?.uniqueId || (sub && paramId ? `${sub}:${paramId}` : '');
    const found = findTelemetrySeriesStore(sub, paramId, colonId, w.id);
    if (found && found.data && found.data.length > 0) {
      const last = found.data[found.data.length - 1]?.value?.[0];
      if (typeof last === 'number' && last > maxTs) {
        maxTs = last;
      }
    }
  }
  if (maxTs > 0) return maxTs;

  if (activeTelemetrySeriesStore && activeTelemetrySeriesStore.length > 0) {
    for (const s of activeTelemetrySeriesStore) {
      if (s && s.data && s.data.length > 0) {
        const last = s.data[s.data.length - 1]?.value?.[0];
        if (typeof last === 'number' && last > maxTs) {
          maxTs = last;
        }
      }
    }
  }
  return maxTs > 0 ? maxTs : Date.now();
}

/** Resolves the earliest telemetry timestamp for a widget across its parameters or global store */
export function getTelemetryEarliestTimestamp(w?: TelemetryPanelConfig): number {
  let minTs = Infinity;
  const params = w?.parameters || [];
  for (const p of params) {
    const sub = (p as any).subsystem || '';
    const paramId = (p as any).paramId || (p as any).paramName || '';
    const colonId = p?.uniqueId || (sub && paramId ? `${sub}:${paramId}` : '');
    const found = findTelemetrySeriesStore(sub, paramId, colonId, w?.id);
    if (found && found.data && found.data.length > 0) {
      const first = found.data[0]?.value?.[0];
      if (typeof first === 'number' && first > 0 && first < minTs) {
        minTs = first;
      }
    }
  }
  if (minTs < Infinity) return minTs;

  if (activeTelemetrySeriesStore && activeTelemetrySeriesStore.length > 0) {
    for (const s of activeTelemetrySeriesStore) {
      if (s && s.data && s.data.length > 0) {
        const first = s.data[0]?.value?.[0];
        if (typeof first === 'number' && first > 0 && first < minTs) {
          minTs = first;
        }
      }
    }
  }
  return minTs < Infinity ? minTs : 0;
}

/** Helper to safely extract tmType from a telemetry data point or series store */
export function getPointTmType(pt?: any, store?: any): string {
  if (pt && typeof pt === 'object' && pt.tmType) {
    return String(pt.tmType);
  }
  if (store && store.tmType) {
    return String(store.tmType);
  }
  if (store && store.eventType && store.eventType !== 'GraphTelemetry' && store.eventType !== 'message') {
    return String(store.eventType);
  }
  return 'TM';
}

/** Build a window bundle from panel + viewport state for chart rendering */
export function bundle(
  w: TelemetryPanelConfig,
  timeframe: number,
  viewport?: TelemetryViewportState
): WindowBundle {
  const params = w.parameters || [];
  const first = params[0];
  const primaryDef = normalizePanelParameter(first);
  const vp = viewport ? viewport : { spanMs: timeframe * 60000, offsetMs: 0 };

  const allSeries: Array<{
    name: string;
    color: string;
    data: any[];
    _def?: TelemetryCatChannelType;
    paramLabel?: string;
    seriesName?: string;
  }> = [];
  let commonTimes: string[] = [];
  let computedEndMs = Date.now();
  let computedStartMs: number | undefined = undefined;

  const widgetLatestTs = getWidgetLatestTimestamp(w);

  params.forEach((param, paramIdx) => {
    const def = normalizePanelParameter(param);
    const subStr = (param as any).subsystem || '';
    const paramIdStr = (param as any).paramId || (param as any).paramName || '';
    const found = findTelemetrySeriesStore(subStr, paramIdStr, param.uniqueId, w.id);
    if (!found || !found.data || found.data.length === 0) return;

    const rawData = found.data;
    const latestTimestamp = widgetLatestTs;
    let startMs: number;
    let endMs: number;

    if (vp.customStartMs != null && vp.customStartMs > 0) {
      startMs = vp.customStartMs;
      endMs = startMs + vp.spanMs;
    } else {
      endMs = vp.offsetMs > 0 ? (latestTimestamp - vp.offsetMs) : latestTimestamp;
      startMs = endMs - vp.spanMs;

      // In live/unlocked mode, if simulation start time is known, clamp window start to not stretch before simulation start
      const simStartMs = globalSimulationStartTimeMs || getTelemetryEarliestTimestamp(w);
      if (simStartMs && simStartMs > 0 && endMs > simStartMs) {
        startMs = Math.max(startMs, simStartMs);
      }
    }
    computedEndMs = endMs;
    computedStartMs = startMs;

    const pointsToUse = rawData.filter((pt) => {
      if (!pt || !pt.value || typeof pt.value[0] !== 'number') return false;
      return pt.value[0] >= startMs && pt.value[0] <= endMs;
    });

    if (pointsToUse.length === 0) {
      if (commonTimes.length === 0) {
        commonTimes = [fmtWinTime(startMs, vp.spanMs), fmtWinTime(endMs, vp.spanMs)];
      }
      return;
    }

    const times = pointsToUse.map((pt) => fmtWinTime(pt.value[0], vp.spanMs));
    if (times.length > commonTimes.length) {
      commonTimes = times;
    }

    const paramLabel = (param as any).label || def.label || 'Parameter';

    const lastPointVal = pointsToUse[pointsToUse.length - 1]?.value?.[1];
    const seriesList: string[] = (param as any)?.series && (param as any).series.length > 0
      ? (param as any).series
      : (def?.series && def.series.length > 0
        ? def.series
        : (Array.isArray(lastPointVal) && lastPointVal.length > 1 ? lastPointVal.map((_, i) => `${paramLabel} ${i}`) : []));

    if (params.length > 1) {
      // Rule 1: Multiple parameters in panel -> Show ONLY parameter label
      const name = paramLabel;
      const data = pointsToUse.map((pt) => {
        const rawVal = Array.isArray(pt.value[1])
          ? (typeof pt.value[1][0] === 'number' ? pt.value[1][0] : 0)
          : (typeof pt.value[1] === 'number' ? pt.value[1] : 0);
        return {
          value: [pt.value[0], rawVal],
          rawVal,
          tMs: pt.value[0],
          paramLabel,
          seriesName: '',
          tmType: getPointTmType(pt, found),
          _def: def,
        };
      });
      allSeries.push({ name, color: getTelemetrySeriesColor(paramIdx), data, _def: def, paramLabel, seriesName: '' });
    } else if (seriesList.length > 0) {
      // Rule 2: Single parameter with multiple series -> Show each series item
      seriesList.forEach((sName, sIdx) => {
        const name = sName;
        const data = pointsToUse.map((pt) => {
          const rawVal = Array.isArray(pt.value[1])
            ? (typeof pt.value[1][sIdx] === 'number' ? pt.value[1][sIdx] : 0)
            : (typeof pt.value[1] === 'number' && sIdx === 0 ? pt.value[1] : 0);
          return {
            value: [pt.value[0], rawVal],
            rawVal,
            tMs: pt.value[0],
            paramLabel,
            seriesName: sName,
            tmType: getPointTmType(pt, found),
            _def: def,
          };
        });
        allSeries.push({ name, color: getTelemetrySeriesColor(paramIdx + sIdx), data, _def: def, paramLabel, seriesName: sName });
      });
    } else {
      // Rule 3: Single parameter without series -> Series name = paramLabel
      const name = paramLabel;
      const data = pointsToUse.map((pt) => {
        const rawVal = typeof pt.value[1] === 'number'
          ? pt.value[1]
          : Array.isArray(pt.value[1])
            ? (typeof pt.value[1][0] === 'number' ? pt.value[1][0] : 0)
            : 0;
        return {
          value: [pt.value[0], rawVal],
          rawVal,
          tMs: pt.value[0],
          paramLabel,
          seriesName: '',
          tmType: getPointTmType(pt, found),
          _def: def,
        };
      });
      allSeries.push({ name, color: getTelemetrySeriesColor(paramIdx), data, _def: def, paramLabel, seriesName: '' });
    }
  });

  return {
    def: primaryDef,
    series: allSeries,
    times: commonTimes,
    spanMs: vp.spanMs,
    endMs: computedEndMs,
    startMs: computedStartMs,
  };
}

/**
 * Checks whether a widget panel has any valid telemetry data points within the current timeframe / viewport window.
 */
export function hasPanelDataInWindow(
  w?: TelemetryPanelConfig | any,
  timeframe: number = 60,
  viewport?: TelemetryViewportState
): boolean {
  if (!w) return false;
  const b = bundle(w, timeframe, viewport);
  if (!b || !b.series || b.series.length === 0) return false;
  return b.series.some((s) => Array.isArray(s.data) && s.data.length > 0);
}

/** Latest scalar value for a widget */
export function latest(w: TelemetryPanelConfig): number {
  const first = w.parameters?.[0];
  const def = normalizePanelParameter(first);
  return liveLatestAll(w, def)[0];
}

/** Viewport-aware latest scalar value — returns value at current viewport position.
 *  When offsetMs > 0 (user scrolled back in time), finds the last data point
 *  at or before the viewport end timestamp instead of the live latest. */
export function latestAtViewport(
  w: TelemetryPanelConfig,
  viewport?: TelemetryViewportState
): number {
  if (!viewport || viewport.offsetMs <= 0) {
    return latest(w);
  }

  const first = w.parameters?.[0];
  if (!first) return latest(w);
  const sub = (first as any).subsystem || '';
  const paramId = (first as any).paramId || (first as any).paramName || '';
  const colonId = first.uniqueId || (sub && paramId ? `${sub}:${paramId}` : '');
  const found = findTelemetrySeriesStore(sub, paramId, colonId, w.id);

  if (!found || !found.data || found.data.length === 0) return latest(w);

  const latestTimestamp = getWidgetLatestTimestamp(w);
  const endMs = latestTimestamp - viewport.offsetMs;

  for (let i = found.data.length - 1; i >= 0; i--) {
    const pt = found.data[i];
    if (!pt || !pt.value || typeof pt.value[0] !== 'number') continue;
    const t = pt.value[0];
    if (t <= endMs) {
      const val = pt.value[1];
      if ((first as any).si != null && Array.isArray(val)) {
        return typeof val[(first as any).si] === 'number' ? val[(first as any).si] : 0;
      }
      if (typeof val === 'number') return val;
      if (Array.isArray(val)) return typeof val[0] === 'number' ? val[0] : 0;
      return 0;
    }
  }

  return latest(w);
}

/**
 * Returns the latest numeric value for an individual column/series item at the active viewport.
 * Supports scalar channels, multi-series/vector channels (via c.si), and historical scrubbing (offsetMs > 0).
 */
export function getColLatestAtViewport(
  c: any,
  w: any,
  viewport?: TelemetryViewportState | { spanMs: number; offsetMs: number }
): number {
  if (!c) return 0;
  const sub = c.subsystem || c.sub || '';
  const paramId = c.paramId || c.paramName || c.tel || '';
  const colonId = c.uniqueId || (sub && paramId ? `${sub}:${paramId}` : '');
  const found = findTelemetrySeriesStore(sub, paramId, colonId, w?.id);
  if (!found || !found.data || found.data.length === 0) return 0;

  const rawData = found.data;
  let targetTime = Infinity;
  if (viewport && viewport.offsetMs > 0) {
    const latestTimestamp = getWidgetLatestTimestamp(w);
    targetTime = latestTimestamp - viewport.offsetMs;
  }

  for (let i = rawData.length - 1; i >= 0; i--) {
    const pt = rawData[i];
    if (!pt || !pt.value || typeof pt.value[0] !== 'number') continue;
    if (pt.value[0] <= targetTime) {
      const val = pt.value[1];
      if (c.si != null && Array.isArray(val)) {
        return typeof val[c.si] === 'number' ? val[c.si] : 0;
      }
      if (typeof val === 'number') return val;
      if (Array.isArray(val)) {
        const idx = c.si ?? 0;
        return typeof val[idx] === 'number' ? val[idx] : 0;
      }
      return 0;
    }
  }
  return 0;
}

/** Latest values for all series of a widget */
export function liveLatestAll(w: TelemetryPanelConfig, def?: TelemetryCatChannelType): number[] {
  const first = w.parameters?.[0];
  if (!first) return [0];
  const sub = (first as any).subsystem || '';
  const paramId = (first as any).paramId || (first as any).paramName || '';
  const found = findTelemetrySeriesStore(sub, paramId, first.uniqueId, w.id);

  if (found && found.data && found.data.length > 0) {
    const lastPt = found.data[found.data.length - 1];
    const val = lastPt.value[1];
    if (Array.isArray(val)) return val;
    if (typeof val === 'number') return [val];
  }

  return [0];
}

/** Determines alert level ('crit' | 'warn' | null) for threshold indicators */
export function alertLevel(w: TelemetryPanelConfig, units?: TelemetryUnitSystem): 'crit' | 'warn' | null {
  if (!w || !['line', 'area', 'kpi', 'gauge', 'radial', 'cchart'].includes(w.graphMode)) return null;
  const params = w.parameters || [];
  if (params.length === 0) return null;

  let highestAlert: 'crit' | 'warn' | null = null;

  for (const param of params) {
    if (!param) continue;
    const sub = (param as any).subsystem || '';
    const paramId = (param as any).paramId || (param as any).paramName || '';
    const def = normalizePanelParameter(param);
    const paramThreshold = (param as any).threshold || {};

    // Respect explicit opt-out: if threshold.on is explicitly false, skip this param
    if (paramThreshold.on === false) continue;

    // Prefer per-param overrides; fall back to channel def — treat def value of 0 as unset
    // (0 as a threshold from the channel catalog means "not configured" for most channels)
    const defWarn = getChannelWarn(def);
    const defCrit = getChannelCrit(def);
    const defDir = getChannelDir(def);

    // Prefer per-param overrides; fall back to channel def — treat def value of 0 as unset
    // (0 as a threshold from the channel catalog means "not configured" for most channels)
    const warn: number | null = paramThreshold.warn != null
      ? paramThreshold.warn
      : (defWarn != null && defWarn !== 0 ? defWarn : null);
    const crit: number | null = paramThreshold.crit != null
      ? paramThreshold.crit
      : (defCrit != null && defCrit !== 0 ? defCrit : null);
    if (warn == null && crit == null) continue;

    // Find telemetry series for this parameter
    const colonId = param.uniqueId || (sub && paramId ? `${sub}:${paramId}` : '');
    const found = findTelemetrySeriesStore(sub, paramId, colonId, w.id);
    if (!found || !found.data || found.data.length === 0) continue;

    const lastPt = found.data[found.data.length - 1];
    if (!lastPt || !lastPt.value) continue;

    const rawVal = lastPt.value[1];
    let val: number | null = null;

    if ((param as any).si != null && Array.isArray(rawVal)) {
      val = typeof rawVal[(param as any).si] === 'number' ? rawVal[(param as any).si] : null;
    } else if (Array.isArray(rawVal)) {
      val = typeof rawVal[0] === 'number' ? rawVal[0] : null;
    } else if (typeof rawVal === 'number') {
      val = rawVal;
    }

    if (val == null || isNaN(val)) continue;

    // Convert value to current display units if units dictionary provided
    const displayVal = units ? (conv(val, def, units) ?? val) : val;

    // Evaluate thresholds against display value
    if (defDir === 'low') {
      if (crit != null && displayVal <= crit) return 'crit';
      if (warn != null && displayVal <= warn) highestAlert = 'warn';
    } else {
      if (crit != null && displayVal >= crit) return 'crit';
      if (warn != null && displayVal >= warn) highestAlert = 'warn';
    }
  }

  return highestAlert;
}

export function colKey(c: any): string {
  const sub = c.subsystem || '';
  const paramId = c.paramId || c.paramName || '';
  return (c.uniqueId ? c.uniqueId : (sub + '.' + paramId)) + (c.si != null ? '.' + c.si : '');
}

export function makeColumns(subOrObj: string | any, paramId?: string): Array<TelemetryCatChannelType> {
  const def = typeof subOrObj === 'object' && subOrObj !== null
    ? normalizePanelParameter(subOrObj)
    : normalizePanelParameter(subOrObj, paramId);

  const sub = def.subsystem || (typeof subOrObj === 'string' ? subOrObj : '');
  const pId = def.paramId || def.paramName || paramId || '';
  const colonId = def.uniqueId || (sub && pId ? `${sub}:${pId}` : '');

  const found = findTelemetrySeriesStore(sub, pId, colonId);
  const lastPtVal = found?.data?.[found.data.length - 1]?.value?.[1];

  let seriesCount = 0;
  if (def.series && def.series.length > 0) {
    seriesCount = def.series.length;
  } else if (Array.isArray(lastPtVal) && lastPtVal.length > 1) {
    seriesCount = lastPtVal.length;
  }

  if (seriesCount > 0) {
    return Array.from({ length: seriesCount }, (_, i) => ({
      ...def,
      si: i,
    } as any));
  }
  return [
    {
      ...def,
      si: null,
    } as any,
  ];
}

/** Returns keys of scalar (cont, pct, multi) channels for a subsystem */
export function scalarChannels(sub: string): string[] {
  const channels = getSubParams(sub);
  return Object.keys(channels).filter(
    (k) => channels[k] && ['cont', 'pct', 'multi'].includes(channels[k].kind)
  );
}

export function tableColDef(c: any): TelemetryCatChannelType {
  if (!c) return DEFAULT_CHANNEL_DEF;
  if (typeof c === 'object') {
    return normalizePanelParameter(c);
  }
  return normalizePanelParameter(c, '');
}

export function tableColLabel(c: any): string {
  if (!c) return 'Parameter';
  const def = tableColDef(c);
  const paramId = c.paramId || c.paramName || def?.paramId || def?.paramName || '';
  const baseLabel = c.label || def?.label || formatParameterLabel(paramId);
  if (c.si != null) {
    const seriesList = c.series || def?.series;
    if (seriesList && seriesList[c.si]) return seriesList[c.si];
    return `${baseLabel} ${c.si}`;
  }
  return baseLabel;
}

export function tableColValueAt(
  c: any,
  t: number,
  panelId?: string
): number {
  if (!c) return 0;
  const sub = c.subsystem || '';
  const paramId = c.paramId || c.paramName || '';
  const colonId = c.uniqueId || (sub && paramId ? `${sub}:${paramId}` : '');
  const found = findTelemetrySeriesStore(sub, paramId, colonId, panelId);

  if (found && found.data && found.data.length > 0) {
    let lo = 0, hi = found.data.length - 1, best = found.data[hi];
    while (lo <= hi) {
      const mid = (lo + hi) >>> 1;
      const mt = found.data[mid].value[0];
      if (mt === t) { best = found.data[mid]; break; }
      if (Math.abs(mt - t) < Math.abs(best.value[0] - t)) best = found.data[mid];
      if (mt < t) lo = mid + 1; else hi = mid - 1;
    }
    const val = best.value[1];
    if (typeof val === 'number') return val;
    if (Array.isArray(val)) return typeof val[c.si ?? 0] === 'number' ? val[c.si ?? 0] : 0;
  }
  return 0;
}

/** Resolves table columns for a widget, expanding single-parameter multi-series channels if needed */
export function resolveTableColumns(w: TelemetryPanelConfig | any): Array<any> {
  const params = w?.parameters && w.parameters.length > 0 ? w.parameters : [];
  if (params.length === 0) return [];
  const cols: any[] = [];
  params.forEach((p: any) => {
    if ((p as any).si == null) {
      const expanded = makeColumns(p);
      if (expanded.length > 1) {
        expanded.forEach((exp) => {
          cols.push({ ...p, ...exp, si: (exp as any).si } as any);
        });
      } else {
        cols.push(p);
      }
    } else {
      cols.push(p);
    }
  });
  return cols;
}

/**
 * Fast, universal timestamp extractor for table views across any workspace.
 * Samples active series with progressive fallback, collecting timestamps in < 1ms even for 1,300+ parameters.
 */
export function getTableTimestamps(
  w: TelemetryPanelConfig,
  timeframe: number = 5,
  viewport?: TelemetryViewportState,
  maxSampleSeries: number = 6
): number[] {
  const cols = resolveTableColumns(w);
  const vis = cols.filter((c) => !(w.hiddenCols || []).includes(colKey(c)));
  if (vis.length === 0) return [];
  const vp = viewport || { spanMs: timeframe * 60000, offsetMs: 0 };

  const timeSet = new Set<number>();
  let sampledCount = 0;

  for (let i = 0; i < vis.length; i++) {
    const c = vis[i];
    const sub = c.subsystem || '';
    const paramId = c.paramId || c.paramName || '';
    const colonId = c.uniqueId || (sub && paramId ? `${sub}:${paramId}` : '');
    const found = findTelemetrySeriesStore(sub, paramId, colonId, w.id);
    if (found && found.data && found.data.length > 0) {
      const rawData = found.data;
      const latestTs = rawData[rawData.length - 1]?.value?.[0] || getWidgetLatestTimestamp(w);
      const endMs = latestTs;
      const startMs = vp.offsetMs > 0
        ? (latestTs - vp.offsetMs - (vp.spanMs || 300000))
        : (latestTs - (vp.spanMs || 300000));

      for (let j = rawData.length - 1; j >= 0; j--) {
        const pt = rawData[j];
        if (pt && pt.value && typeof pt.value[0] === 'number') {
          const t = pt.value[0];
          if (t >= startMs && t <= endMs) {
            timeSet.add(t);
          } else if (t < startMs) {
            break;
          }
        }
      }

      sampledCount++;
      const minRequired = vp.offsetMs > 0 ? 500 : 50;
      if (sampledCount >= maxSampleSeries && timeSet.size >= minRequired) {
        break; // Collected sufficient timestamps from active series
      }
    }
  }

  // Fallback: if timeSet is empty, take last 50 points from first available series
  if (timeSet.size === 0) {
    for (let i = 0; i < vis.length; i++) {
      const c = vis[i];
      const sub = c.subsystem || '';
      const paramId = c.paramId || c.paramName || '';
      const colonId = c.uniqueId || (sub && paramId ? `${sub}:${paramId}` : '');
      const found = findTelemetrySeriesStore(sub, paramId, colonId, w.id);
      if (found && found.data && found.data.length > 0) {
        found.data.slice(-50).forEach((pt) => {
          if (pt && pt.value && typeof pt.value[0] === 'number') {
            timeSet.add(pt.value[0]);
          }
        });
        if (timeSet.size > 0) break;
      }
    }
  }

  const maxRows = vp.offsetMs > 0 ? 1000 : (timeframe >= 60 ? 200 : timeframe >= 15 ? 100 : 50);
  return Array.from(timeSet).sort((a, b) => b - a).slice(0, maxRows);
}

export function streamTableRows(
  w: TelemetryPanelConfig,
  units: TelemetryUnitSystem,
  precision: number,
  timeframe: number = 5,
  viewport?: TelemetryViewportState
) {
  const cols = resolveTableColumns(w);
  const vis = cols.filter((c) => !(w.hiddenCols || []).includes(colKey(c)));
  const times = getTableTimestamps(w, timeframe, viewport);
  if (times.length === 0) return [];

  return times.map((ms) => {
    const cells = vis.map((c) => {
      const def = tableColDef(c);
      const val = tableColValueAt(c, ms, w.id);
      return {
        v: fmt(val, def, units, precision),
        placeholder: val === undefined || val === null,
      };
    });
    return { ms, t: fmtClock(ms), cells };
  });
}

export function transposedTable(
  w: TelemetryPanelConfig,
  units: TelemetryUnitSystem,
  precision: number,
  timeframe: number = 5,
  viewport?: TelemetryViewportState
) {
  const cols = resolveTableColumns(w);
  const vis = cols.filter((c) => !(w.hiddenCols || []).includes(colKey(c)));
  const times = getTableTimestamps(w, timeframe, viewport);

  const paramRows = vis.map((c) => {
    const def = tableColDef(c);
    const cells = times.map((ms) => {
      const val = tableColValueAt(c, ms, w.id);
      return { v: fmt(val, def, units, precision), placeholder: val === undefined || val === null };
    });
    return { c, cells };
  });

  return { times, paramRows };
}
/**
 * Merge two arrays of TelemetryDataPoint with Map deduplication,
 * ascending timestamp sort (t0 < t1 < t2), and optional buffer capping.
 */
export function mergeAndSortTelemetryData(
  existingData: TelemetryDataPoint[] = [],
  newData: TelemetryDataPoint[] = [],
  maxPoints: number = 5000
): TelemetryDataPoint[] {
  const map = new Map<number, TelemetryDataPoint>();

  const normalizePt = (pt: TelemetryDataPoint): TelemetryDataPoint => {
    if (!pt || !pt.value || typeof pt.value[0] !== 'number') return pt;
    const t = pt.value[0] < 1e11 ? pt.value[0] * 1000 : pt.value[0];
    return { ...pt, value: [t, pt.value[1]] };
  };

  for (const pt of existingData) {
    const norm = normalizePt(pt);
    if (norm && norm.value && typeof norm.value[0] === 'number') {
      map.set(norm.value[0], norm);
    }
  }

  for (const pt of newData) {
    const norm = normalizePt(pt);
    if (norm && norm.value && typeof norm.value[0] === 'number') {
      map.set(norm.value[0], norm);
    }
  }

  const sorted = Array.from(map.values()).sort((a, b) => a.value[0] - b.value[0]);
  if (sorted.length > maxPoints) {
    return sorted.slice(sorted.length - maxPoints);
  }
  return sorted;
}
/**
 * Strips frontend-only properties (like `si`) from a panel parameter object
 * before persisting configuration payload to backend REST API.
 */
export function sanitizePanelParameterForBackend(param: any): any {
  if (!param || typeof param !== 'object') return param;
  const { si, ...clean } = param;
  return clean;
}

/**
 * Sanitizes an entire workspace config object or array of workspace tabs for backend persistence.
 * Removes `si` and deduplicates parameters in panel widgets.
 */
export function sanitizeWorkspaceForBackend(config: any): any {
  if (!config || typeof config !== 'object') return config;

  const clone = JSON.parse(JSON.stringify(config));

  const sanitizePanels = (panels: any[]) => {
    if (!Array.isArray(panels)) return panels;
    return panels.map((panel) => {
      if (!panel || typeof panel !== 'object') return panel;
      // Strip transient client-side UI fields (e.g. searchQuery, search, orient, order) so they are never sent to backend API
      const { searchQuery, search, orient, order, ...cleanPanel } = panel;
      if (Array.isArray(cleanPanel.parameters)) {
        const cleanedParams: any[] = [];
        const seenKeys = new Set<string>();

        cleanPanel.parameters.forEach((p: any) => {
          if (!p || typeof p !== 'object') return;
          const { si, ...rest } = p;
          const sub = rest.subsystem || '';
          const pId = rest.paramId || rest.paramName || '';
          const uId = rest.uniqueId || `${sub}:${pId}`;
          const key = `${sub}:${pId}:${uId}`;

          if (!seenKeys.has(key)) {
            seenKeys.add(key);
            cleanedParams.push(rest);
          }
        });

        cleanPanel.parameters = cleanedParams;
      }
      return cleanPanel;
    });
  };

  if (Array.isArray(clone)) {
    return clone.map((ws) => ({
      ...ws,
      panels: sanitizePanels(ws?.panels),
    }));
  }

  if (Array.isArray(clone.workspaces)) {
    clone.workspaces = clone.workspaces.map((ws: any) => ({
      ...ws,
      panels: sanitizePanels(ws?.panels),
    }));
  }

  return clone;
}

function canonicalizeTelemetryValue(val: any): any {
  if (val === null || val === undefined) return undefined;
  if (typeof val !== 'object') return val;
  if (Array.isArray(val)) {
    return val.map(canonicalizeTelemetryValue).filter((v) => v !== undefined);
  }
  const sortedObj: Record<string, any> = {};
  const keys = Object.keys(val).sort();
  for (const k of keys) {
    const v = val[k];
    if (v !== undefined) {
      sortedObj[k] = canonicalizeTelemetryValue(v);
    }
  }
  return sortedObj;
}

/**
 * Performs a deep canonical comparison between two workspace configurations
 * or arrays of workspaces to determine if the state matches the saved snapshot.
 * Ignores key order, transient properties, and standardizes numeric grid bounds.
 */
export function areWorkspacesEqual(a: any, b: any): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  try {
    const rawWsA = Array.isArray(a) ? a : Array.isArray(a?.workspaces) ? a.workspaces : [];
    const rawWsB = Array.isArray(b) ? b : Array.isArray(b?.workspaces) ? b.workspaces : [];

    if (rawWsA.length !== rawWsB.length) return false;

    const cleanA = sanitizeWorkspaceForBackend(rawWsA);
    const cleanB = sanitizeWorkspaceForBackend(rawWsB);

    const normA = (Array.isArray(cleanA) ? cleanA : []).map((ws: any) => ({
      id: String(ws?.id || ''),
      name: String(ws?.name || ''),
      panels: (Array.isArray(ws?.panels) ? ws.panels : []).map((p: any) => ({
        id: String(p?.id || ''),
        graphMode: String(p?.graphMode || ''),
        name: String(p?.name || ''),
        x: Number(p?.x ?? 0),
        y: Number(p?.y ?? 0),
        w: Number(p?.w ?? 4),
        h: Number(p?.h ?? 3),
        minW: p?.minW != null ? Number(p.minW) : undefined,
        minH: p?.minH != null ? Number(p.minH) : undefined,
        parameters: (Array.isArray(p?.parameters) ? p.parameters : []).map((param: any) => ({
          subsystem: String(param?.subsystem || ''),
          paramId: String(param?.paramId || param?.paramName || ''),
          paramName: String(param?.paramName || param?.paramId || ''),
          uniqueId: String(param?.uniqueId || ''),
          unit: param?.unit != null ? String(param.unit) : undefined,
          thresholdOn: param?.threshold?.on !== false,
        })),
        hiddenCols: Array.isArray(p?.hiddenCols) ? p.hiddenCols : undefined,
      })),
    }));

    const normB = (Array.isArray(cleanB) ? cleanB : []).map((ws: any) => ({
      id: String(ws?.id || ''),
      name: String(ws?.name || ''),
      panels: (Array.isArray(ws?.panels) ? ws.panels : []).map((p: any) => ({
        id: String(p?.id || ''),
        graphMode: String(p?.graphMode || ''),
        name: String(p?.name || ''),
        x: Number(p?.x ?? 0),
        y: Number(p?.y ?? 0),
        w: Number(p?.w ?? 4),
        h: Number(p?.h ?? 3),
        minW: p?.minW != null ? Number(p.minW) : undefined,
        minH: p?.minH != null ? Number(p.minH) : undefined,
        parameters: (Array.isArray(p?.parameters) ? p.parameters : []).map((param: any) => ({
          subsystem: String(param?.subsystem || ''),
          paramId: String(param?.paramId || param?.paramName || ''),
          paramName: String(param?.paramName || param?.paramId || ''),
          uniqueId: String(param?.uniqueId || ''),
          unit: param?.unit != null ? String(param.unit) : undefined,
          thresholdOn: param?.threshold?.on !== false,
        })),
        hiddenCols: Array.isArray(p?.hiddenCols) ? p.hiddenCols : undefined,
      })),
    }));

    return JSON.stringify(canonicalizeTelemetryValue(normA)) === JSON.stringify(canonicalizeTelemetryValue(normB));
  } catch (e) {
    return false;
  }
}

export { conv, unitOf, fmt };

